import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import AxiosInstance from "../../AxiosInstance";
import {
    Grid, Snackbar, Typography, Button, TextField,
    Alert, Container, Paper, Box, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";
import config, { environment } from '../../config';

function AddRunningRoute() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const GlobalState = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const fileInputRef = useRef(null);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('5K City');
    const [distance, setDistance] = useState('');
    const [points, setPoints] = useState([]); // Array of [lat, lng]
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    function MapComponent() {
        const map = useMap();
        map.on('click', (e) => {
            setPoints(prev => [...prev, [e.latlng.lat, e.latlng.lng]]);
        });
        return null;
    }

    const parseGPX = (text) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const trkpts = xmlDoc.getElementsByTagName("trkpt");
        const newPoints = [];
        for (let i = 0; i < trkpts.length; i++) {
            const lat = parseFloat(trkpts[i].getAttribute("lat"));
            const lon = parseFloat(trkpts[i].getAttribute("lon"));
            if (!isNaN(lat) && !isNaN(lon)) {
                newPoints.push([lat, lon]);
            }
        }
        return newPoints;
    };

    const parseKML = (text) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const coordinatesNodes = xmlDoc.getElementsByTagName("coordinates");
        const newPoints = [];
        for (let i = 0; i < coordinatesNodes.length; i++) {
            const coordsText = coordinatesNodes[i].textContent.trim();
            // Handle coordinates separated by space or newline
            const coordsArray = coordsText.split(/[\s\n\r]+/);
            coordsArray.forEach(coordStr => {
                if (!coordStr) return;
                const parts = coordStr.split(',');
                if (parts.length >= 2) {
                    const lon = parseFloat(parts[0]);
                    const lat = parseFloat(parts[1]);
                    if (!isNaN(lat) && !isNaN(lon)) {
                        newPoints.push([lat, lon]);
                    }
                }
            });
        }
        return newPoints;
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            let newPoints = [];
            if (file.name.toLowerCase().endsWith('.gpx')) {
                newPoints = parseGPX(text);
            } else if (file.name.toLowerCase().endsWith('.kml')) {
                newPoints = parseKML(text);
            }

            if (newPoints.length > 0) {
                setPoints(newPoints);
                setError('');
            } else {
                setError(t('Running.Error.InvalidFile', 'Nem sikerült érvényes koordinátákat beolvasni a fájlból!'));
            }
        };
        reader.readAsText(file);
    };

    function MapFlyTo({ positions }) {
        const map = useMap();
        useEffect(() => {
            if (positions.length > 0) {
                const bounds = L.latLngBounds(positions);
                map.flyToBounds(bounds, { padding: [20, 20] });
            }
        }, [positions, map]);
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (points.length < 2) {
            setError(t('Running.Error.MinPoints', 'Legalább két pontot meg kell adni az útvonalhoz!'));
            return;
        }
        setLoading(true);

        const token = GlobalState.jwtAccessToken || localStorage.getItem('token');
        
        // Convert points to WKT MultiLineString (simple case with one LineString inside)
        // MultiLineString((x1 y1, x2 y2, ...))
        const wktPoints = points.map(p => `${p[1]} ${p[0]}`).join(', ');
        const wkt = `SRID=4326;MULTILINESTRING((${wktPoints}))`;

        const formData = {
            name: name,
            description: description,
            category: category,
            distance: distance ? parseFloat(distance) : null,
            geom: wkt
        };

        try {
            await AxiosInstance.post(config[environment].createRunningRouteEndpoint, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setSuccess(true);
            setTimeout(() => navigate(-1), 2000);
        } catch (err) {
            setError(t('Common.Error', 'Hiba történt a mentés során.'));
            setLoading(false);
        }
    };

    if (!GlobalState.userIsLogged) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, mt: '100px' }}>
                <Alert severity="warning">{t('Administration.LoginRequired')}</Alert>
                <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>{t('Administration.LoginButton')}</Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4, mt: '100px' }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#105b79', fontWeight: 'bold' }}>
                    {t('Running.AddNew', 'Új futóútvonal hozzáadása')}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField fullWidth label={t('Common.Name')} value={name} onChange={e => setName(e.target.value)} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('Common.Category')}</InputLabel>
                                <Select value={category} label={t('Common.Category')} onChange={e => setCategory(e.target.value)}>
                                    <MenuItem value="10K City">10K City</MenuItem>
                                    <MenuItem value="10K Cross">10K Cross</MenuItem>
                                    <MenuItem value="5K City">5K City</MenuItem>
                                    <MenuItem value="Félmaraton">Félmaraton</MenuItem>
                                    <MenuItem value="Maraton">Maraton</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" label={t('Common.Distance') + " (km)"} value={distance} onChange={e => setDistance(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={3} label={t('Common.Description')} value={description} onChange={e => setDescription(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1">
                                    {t('Running.DrawRoute', 'Rajzolja meg az útvonalat a térképen kattintással!')}
                                </Typography>
                                <Box>
                                    <input
                                        type="file"
                                        accept=".gpx,.kml"
                                        style={{ display: 'none' }}
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<CloudUploadIcon />}
                                        onClick={() => fileInputRef.current.click()}
                                        sx={{ mr: 1 }}
                                    >
                                        GPX/KML feltöltés
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setPoints([])}
                                        disabled={points.length === 0}
                                    >
                                        {t('Common.Clear', 'Törlés')}
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={{ 
                                height: '400px', 
                                width: '100%', 
                                mb: 2,
                                '& .leaflet-container': {
                                    cursor: 'crosshair'
                                }
                            }}>
                                <MapContainer center={[45.8643, 25.7848]} zoom={13} style={{ height: '100%', borderRadius: '8px' }}>
                                    <TileLayer url={config[environment].osmTiles} />
                                    <MapComponent />
                                    <MapFlyTo positions={points} />
                                    {points.length > 1 && <Polyline positions={points} color="magenta" />}
                                </MapContainer>
                            </Box>
                            <Typography variant="caption">{points.length} pont lehelyezve</Typography>
                        </Grid>
                    </Grid>
                    {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                        <Button type="submit" variant="contained" disabled={loading} sx={{ bgcolor: '#105b79', flex: 1 }}>
                            {loading ? <CircularProgress size={24} /> : t('Common.Submit')}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ flex: 1 }}>{t('Common.Cancel')}</Button>
                    </Box>
                </form>
            </Paper>
            <Snackbar open={success} autoHideDuration={3000}><Alert severity="success">{t('Common.Success')}</Alert></Snackbar>
        </Container>
    );
}

export default AddRunningRoute;
